<template>

  <div @click="showModal = true"
       class=" flex items-center text-end text-xs text-secondary px-2 gap-2 cursor-pointer hover:bg-hover rounded-full ring-hover hover:ring-8">
    <span>{{ formattedLastUpdated }}</span>
    <md-icon class="text-secondary" style="--md-icon-size: 16px;"><IconInfoOutline /></md-icon>
  </div>

  <teleport to="body">
    <md-dialog
      :open="showModal"
      @close="showModal = false"
      quick
    >
      <div slot="headline">{{ $t('forecastInfo.details') }}</div>
      <div slot="content">
        <p class="text-secondary text-sm mb-2 px-6 leading-relaxed">{{ $t('forecastInfo.explanation') }}</p>
        <md-list>
          <md-list-item>
            <md-icon slot="start"><IconDns /></md-icon>
            <div slot="headline">{{ $t('forecastInfo.computed') }}</div>
            <div slot="supporting-text">{{ formattedComputedTime }}</div>
          </md-list-item>

          <md-list-item>
            <md-icon slot="start"><IconDownload /></md-icon>
            <div slot="headline">{{ $t('forecastInfo.loaded') }}</div>
            <div slot="supporting-text">{{ formattedLoadedTime }}</div>
          </md-list-item>

          <md-list-item v-if="forecast.expires">
            <md-icon slot="start"><IconUpdate /></md-icon>
            <div slot="headline">{{ $t('forecastInfo.expires') }}</div>
            <div slot="supporting-text">{{ formattedExpiresTime }}</div>
          </md-list-item>
        </md-list>
      </div>
      <div slot="actions">
        <md-text-button @click="showModal = false">{{ $t('forecastInfo.close') }}</md-text-button>
      </div>
    </md-dialog>
  </teleport>
</template>

<script setup>
import {ref, computed} from 'vue';
import {useI18n} from 'vue-i18n';

// Material Web Imports
import '@material/web/dialog/dialog.js';
import '@material/web/list/list.js';
import '@material/web/list/list-item.js';
import '@material/web/button/text-button.js';
import '@material/web/icon/icon.js';

import IconInfoOutline from '~icons/material-symbols/info-outline-rounded';
import IconDns from '~icons/material-symbols/host-rounded';
import IconDownload from '~icons/material-symbols/download-rounded';
import IconUpdate from '~icons/material-symbols/update-rounded';


const props = defineProps({
  forecast: {
    type: Object,
    required: true,
  },
});

const {t} = useI18n();
const showModal = ref(false);

const formattedLastUpdated = computed(() => {
  if (props.forecast.expires) {
    const date = new Date(props.forecast.expires * 1000);
    return t('home.validUntil', {
      time: date.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'})
    });
  } else {
    const date = new Date(props.forecast.loadedAt * 1000);
    return t('home.loadedAt', {
      time: date.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'})
    });
  }
});


const formattedComputedTime = computed(() => {
  if (!props.forecast?.computed) return {date: '', time: ''};
  const date = new Date(props.forecast.computed * 1000);
  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    month: 'short',
    day: 'numeric',
  });
});


const formattedExpiresTime = computed(() => {
  if (!props.forecast?.expires) return '';
  const date = new Date(props.forecast.expires * 1000);
  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    month: 'short',
    day: 'numeric',
  });
});

const formattedLoadedTime = computed(() => {
  if (!props.forecast?.loadedAt) return '';
  const date = new Date(props.forecast.loadedAt * 1000);
  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    month: 'short',
    day: 'numeric',
  });
});
</script>

<style scoped>
md-list {
  background: transparent;
  --md-list-container-color: transparent;
}
md-list-item {
  background: transparent;
  --md-list-item-container-color: transparent;
}
</style>
